import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import chevronRight from "../../assets/chevron-right.svg";
import { useAuth } from "../../utils/authContext.js";
import {
    getClients,
    getTelegramClients,
    verifyTelegramCustomer
} from "../../services/client-service.js";
import LoadingSpinner from "../../components/LoadingSpinner/loadingSpinner.js";
import Toast from "../../components/Toast/toast.js";
import ErrorDisplay from "../../components/comman/ErrorDisplay.js";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation } from "../../utils/translate.js";
import Pagination from "../../components/comman/pagination.js";
import RowsPerPageSelect from "../../components/comman/RowsPerPageSelect.js";
import CompanyManagement from "./CompanyManagement.js";

const mergeUsersWithTelegram = (users, telegramList) => {
    const byUserId = new Map();
    (telegramList || []).forEach((tg) => {
        if (tg.userId != null) {
            byUserId.set(Number(tg.userId), tg);
        }
    });
    return (users || []).map((u) => {
        const telegram = byUserId.get(Number(u.id)) || null;
        return {
            ...u,
            telegram,
            companyId: telegram?.companyId ?? u.companyId ?? null,
            companyName: telegram?.companyName ?? u.companyName ?? null
        };
    });
};

const buildCompanySummary = (telegramList, users) => {
    const source = telegramList?.length ? telegramList : users || [];
    const map = new Map();
    source.forEach((row) => {
        const companyId = row.companyId ?? null;
        const key = companyId == null ? "none" : String(companyId);
        const prev = map.get(key);
        if (prev) {
            prev.clientsCount += 1;
            if (!prev.companyName && row.companyName) {
                prev.companyName = row.companyName;
            }
        } else {
            map.set(key, {
                companyId,
                companyName: row.companyName || null,
                clientsCount: 1
            });
        }
    });
    const rows = [...map.values()];
    rows.sort((a, b) => {
        if (a.companyId == null) return 1;
        if (b.companyId == null) return -1;
        return (a.companyName || "").localeCompare(b.companyName || "", undefined, {
            sensitivity: "base"
        });
    });
    return rows;
};

const compareClients = (a, b, sortBy, sortOrder) => {
    const dir = sortOrder === "asc" ? 1 : -1;
    switch (sortBy) {
        case "name":
            return (
                dir *
                (a.fullName || a.phoneNumber || "").localeCompare(
                    b.fullName || b.phoneNumber || "",
                    undefined,
                    { sensitivity: "base" }
                )
            );
        case "ordersCount": {
            const ac = a.ordersCount ?? a.orderIds?.length ?? 0;
            const bc = b.ordersCount ?? b.orderIds?.length ?? 0;
            return dir * (ac - bc);
        }
        case "totalSpent":
            return dir * ((a.purchasedTotalSum ?? 0) - (b.purchasedTotalSum ?? 0));
        default: {
            const at = a.latestOrderDate ? new Date(a.latestOrderDate).getTime() : 0;
            const bt = b.latestOrderDate ? new Date(b.latestOrderDate).getTime() : 0;
            return dir * (at - bt);
        }
    }
};

const groupClientsByCompany = (clients) => {
    const groups = new Map();
    clients.forEach((client) => {
        const key = client.companyId == null ? "none" : String(client.companyId);
        if (!groups.has(key)) {
            groups.set(key, {
                key,
                companyId: client.companyId ?? null,
                companyName: client.companyName || null,
                clients: []
            });
        }
        groups.get(key).clients.push(client);
    });
    const named = [];
    let none = null;
    groups.forEach((g) => {
        if (g.companyId == null) none = g;
        else named.push(g);
    });
    named.sort((a, b) =>
        (a.companyName || "").localeCompare(b.companyName || "", undefined, { sensitivity: "base" })
    );
    return none ? [...named, none] : named;
};

const SORT_OPTIONS = [
    { value: "latestOrderDate", labelKey: "clients.sortLatestOrder", fallback: "Latest order" },
    { value: "ordersCount", labelKey: "clients.sortOrdersCount", fallback: "Orders count" },
    { value: "totalSpent", labelKey: "clients.sortTotalSpent", fallback: "Total spent" },
    { value: "name", labelKey: "clients.sortName", fallback: "Name" }
];

const NO_COMPANY = "none";

const toIsoDate = (date) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/**
 * Link to the promo code form prefilled for one client: targeted at their phone, single use,
 * valid for 30 days from today, with a suggested code the chef can change.
 */
export const buildGiveCouponUrl = (client) => {
    const params = new URLSearchParams();
    if (client?.phoneNumber) params.set("userPhoneNumber", client.phoneNumber.replace(/\s/g, ""));
    const digits = (client?.phoneNumber || "").replace(/\D/g, "").slice(-4);
    const suffix = Math.random().toString(36).slice(2, 5).toUpperCase();
    params.set("code", `GIFT${digits}${suffix}`);
    params.set("maxUses", "1");
    params.set("maxUsesPerUser", "1");
    const today = new Date();
    const in30Days = new Date(today);
    in30Days.setDate(today.getDate() + 30);
    params.set("validFrom", toIsoDate(today));
    params.set("validTo", toIsoDate(in30Days));
    return `/promocodes/create?${params.toString()}`;
};

const Clients = () => {
    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [debouncedSearchText, setDebouncedSearchText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const [verifyingTelegram, setVerifyingTelegram] = useState(false);
    const { chefData } = useAuth();
    const { language } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const isInitialMount = useRef(true);
    const hasSearched = useRef(false);
    const isFetching = useRef(false);
    const toastRef = useRef(null);
    const hasProcessedSearchPhone = useRef(false);
    const [activeTab, setActiveTab] = useState("clients");
    const [refreshNonce, setRefreshNonce] = useState(0);
    const [companyFilter, setCompanyFilter] = useState("");
    const [sortBy, setSortBy] = useState("latestOrderDate");
    const [sortOrder, setSortOrder] = useState("desc");
    const [telegramList, setTelegramList] = useState([]);
    const [companySummaryList, setCompanySummaryList] = useState([]);

    const hasTelegramBot = chefData?.hasTelegramBot === true;

    useEffect(() => {
        if (!hasTelegramBot && activeTab === "companies") {
            setActiveTab("clients");
        }
    }, [hasTelegramBot, activeTab]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        if (searchText.trim() !== "") {
            hasSearched.current = true;
        }

        const timer = setTimeout(() => {
            setDebouncedSearchText(searchText);
            if (searchText !== "") {
                setCurrentPage(1);
            }
        }, 500);

        return () => {
            clearTimeout(timer);
        };
    }, [searchText]);

    useEffect(() => {
        if (!chefData) return;
        if (isFetching.current) return;

        const fetchClients = async () => {
            isFetching.current = true;
            setLoading(true);
            setError(null);
            try {
                const offset = (currentPage - 1) * itemsPerPage;
                const searchParam =
                    hasSearched.current && debouncedSearchText.trim()
                        ? debouncedSearchText.trim()
                        : undefined;
                const data = await getClients({
                    limit: itemsPerPage,
                    offset,
                    search: searchParam
                });

                setClients(data?.users || []);
                setTotalCount(data?.count || 0);
                setError(null);
            } catch (err) {
                console.error("Error fetching clients:", err);
                setError(err);
                setClients([]);
                setTotalCount(0);
                toastRef.current?.addToast(
                    getTranslation("clients.fetchError", language) ||
                        "Failed to fetch clients",
                    "error"
                );
            } finally {
                setLoading(false);
                isFetching.current = false;
            }
        };

        fetchClients();
    }, [
        chefData,
        currentPage,
        itemsPerPage,
        debouncedSearchText,
        language,
        refreshNonce
    ]);

    useEffect(() => {
        if (!chefData) return;
        if (!hasTelegramBot) {
            setTelegramList([]);
            setCompanySummaryList([]);
            return;
        }
        let cancelled = false;
        const companyId =
            companyFilter && companyFilter !== NO_COMPANY ? companyFilter : undefined;
        getTelegramClients(companyId ? { companyId } : undefined)
            .then((rows) => {
                if (cancelled) return;
                setTelegramList(rows);
                if (!companyId) setCompanySummaryList(rows);
            })
            .catch((err) => {
                console.error("Error fetching Telegram clients:", err);
                if (!cancelled) setTelegramList([]);
                toastRef.current?.addToast(
                    getTranslation("clients.fetchTelegramError", language) ||
                        "Failed to load Telegram client data",
                    "error"
                );
            });
        return () => {
            cancelled = true;
        };
    }, [chefData, hasTelegramBot, refreshNonce, language, companyFilter]);

    const mergedClients = useMemo(
        () => mergeUsersWithTelegram(clients, telegramList),
        [clients, telegramList]
    );

    const companySummary = useMemo(
        () => buildCompanySummary(companySummaryList, mergedClients),
        [companySummaryList, mergedClients]
    );

    const hasCompanies = companySummary.some((row) => row.companyId != null);

    const visibleClients = useMemo(() => {
        let list = mergedClients;
        if (companyFilter === NO_COMPANY) {
            list = list.filter((c) => c.companyId == null);
        } else if (companyFilter) {
            list = list.filter((c) => String(c.companyId) === companyFilter);
        }
        return [...list].sort((a, b) => compareClients(a, b, sortBy, sortOrder));
    }, [mergedClients, companyFilter, sortBy, sortOrder]);

    const groupedClients = useMemo(
        () => groupClientsByCompany(visibleClients),
        [visibleClients]
    );

    const selectCompany = (value) => {
        setCompanyFilter(value);
    };

    const dateLocale = language === "am" ? "hy-AM" : language === "ru" ? "ru-RU" : "en-US";
    const formatDate = (value) => (value ? new Date(value).toLocaleDateString(dateLocale) : "—");

    const handleGiveCoupon = (client) => {
        navigate(buildGiveCouponUrl(client));
    };

    useEffect(() => {
        const searchPhone = location.state?.searchPhone;

        if (!searchPhone) {
            hasProcessedSearchPhone.current = false;
            return;
        }

        if (searchPhone && !hasProcessedSearchPhone.current) {
            hasProcessedSearchPhone.current = true;
            setSearchText(searchPhone);
            hasSearched.current = true;
            setDebouncedSearchText(searchPhone);
            setCurrentPage(1);
        }
    }, [location.state]);

    useEffect(() => {
        const searchPhone = location.state?.searchPhone;

        if (
            searchPhone &&
            mergedClients.length > 0 &&
            !selectedClient &&
            !loading &&
            !isFetching.current &&
            debouncedSearchText === searchPhone
        ) {
            const normalizedSearchPhone = searchPhone.replace(/\s/g, "");
            const matchingClient = mergedClients.find((client) => {
                if (!client.phoneNumber) return false;
                const normalizedClientPhone = client.phoneNumber.replace(/\s/g, "");
                return (
                    client.phoneNumber === searchPhone ||
                    normalizedClientPhone === normalizedSearchPhone
                );
            });

            if (matchingClient) {
                setSelectedClient(matchingClient);
                window.scrollTo({ top: 0, behavior: "smooth" });
                navigate(location.pathname, { replace: true, state: {} });
            }
        }
    }, [
        mergedClients,
        location.state,
        location.pathname,
        selectedClient,
        loading,
        navigate,
        debouncedSearchText
    ]);

    useEffect(() => {
        setSelectedClient((prev) => {
            if (!prev?.id) return prev;
            return mergedClients.find((c) => c.id === prev.id) || prev;
        });
    }, [mergedClients]);

    const handleClientClick = (client) => {
        setSelectedClient(client);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleBackToList = () => {
        setSelectedClient(null);
    };

    const handleViewOrder = (orderId) => {
        const firstEmail = selectedClient?.email;
        navigate(`/orders/${orderId}`, {
            state: {
                fromPage: "clients",
                clientId: selectedClient?.phoneNumber || firstEmail
            }
        });
    };

    const formatPrice = (price) => {
        if (typeof price === "number") {
            return price.toLocaleString("en-US");
        }
        return price;
    };

    const calculateClientStats = () => {
        const orderCount = selectedClient?.ordersCount ?? selectedClient?.orderIds?.length ?? 0;
        const totalSpent = selectedClient?.purchasedTotalSum ?? 0;
        const averageOrderValue =
            selectedClient?.averageOrderValue ?? (orderCount > 0 ? totalSpent / orderCount : 0);
        let lastOrderDate = null;
        if (selectedClient?.latestOrderDate) {
            lastOrderDate = new Date(selectedClient.latestOrderDate);
        }

        return {
            totalOrders: orderCount,
            totalSpent,
            averageOrderValue,
            lastOrderDate
        };
    };

    const clientStats = calculateClientStats();

    const patchClientTelegramVerified = useCallback((userId, telegramCustomerId) => {
        setClients((prev) =>
            prev.map((c) => {
                if (c.id !== userId) return c;
                if (!c.telegram || c.telegram.id !== telegramCustomerId) return c;
                return {
                    ...c,
                    telegram: { ...c.telegram, isVerified: true }
                };
            })
        );
        setSelectedClient((prev) => {
            if (!prev || prev.id !== userId) return prev;
            if (!prev.telegram || prev.telegram.id !== telegramCustomerId) return prev;
            return {
                ...prev,
                telegram: { ...prev.telegram, isVerified: true }
            };
        });
    }, []);

    const handleVerifyTelegram = async () => {
        const tg = selectedClient?.telegram;
        if (!tg?.id) return;
        setVerifyingTelegram(true);
        try {
            await verifyTelegramCustomer(tg.id, true);
            patchClientTelegramVerified(selectedClient.id, tg.id);
            toastRef.current?.addToast(
                getTranslation("clients.verifySuccess", language) ||
                    "Customer verified successfully",
                "success"
            );
        } catch (e) {
            console.error(e);
            toastRef.current?.addToast(
                getTranslation("clients.verifyError", language) ||
                    "Failed to verify customer",
                "error"
            );
        } finally {
            setVerifyingTelegram(false);
        }
    };

    useEffect(() => {
        if (selectedClient) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [selectedClient]);

    if (selectedClient) {
        const needsVerification =
            selectedClient.telegram &&
            selectedClient.telegram.isVerified !== true;

        return (
            <div className="pl-2">
                <h1 className="text-xl font-bold mb-4 text-gray-900 dark:text-[#c9d1d9]">
                    {getTranslation("menuItems.clients", language)}
                </h1>

                <button
                    onClick={handleBackToList}
                    className="flex items-center text-gray-700 dark:text-[#b1bac4] hover:text-gray-900 dark:hover:text-[#c9d1d9] mb-4 sm:mb-6 font-semibold text-sm sm:text-base"
                >
                    <svg
                        className="w-4 h-4 sm:w-5 sm:h-5 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 19l-7-7 7-7"
                        />
                    </svg>
                    {getTranslation("common.back", language) || "Back"}
                </button>

                <div className="bg-white dark:bg-[#161b22] rounded-lg shadow-sm p-4 sm:p-5 mb-4 sm:mb-6 border border-gray-100 dark:border-[#30363d]">
                    <div className="flex items-center mb-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white dark:bg-[#0d1117] flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0 border dark:border-[#30363d]">
                            <svg
                                className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600 dark:text-[#b1bac4]"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2.5}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                />
                            </svg>
                        </div>
                        <div className="min-w-0 flex-1">
                            <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-[#c9d1d9]">
                                {getTranslation("clients.clientInfo", language) ||
                                    "Client Information"}
                            </h2>
                            <p className="text-xs sm:text-sm text-gray-500 dark:text-[#8b949e] mt-1 font-medium">
                                {clientStats.totalOrders}{" "}
                                {getTranslation("clients.totalOrders", language) ||
                                    "Total Orders"}
                                {selectedClient.companyName && (
                                    <>
                                        {" · "}
                                        {selectedClient.companyName}
                                    </>
                                )}
                                {selectedClient.firstOrderDate && (
                                    <>
                                        {" · "}
                                        {getTranslation("clients.firstOrder", language) ||
                                            "First order"}
                                        {": "}
                                        {formatDate(selectedClient.firstOrderDate)}
                                    </>
                                )}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => handleGiveCoupon(selectedClient)}
                            className="px-3 py-1.5 text-xs font-semibold rounded-md bg-customOrange text-white hover:opacity-90 whitespace-nowrap ml-3 flex-shrink-0"
                        >
                            {getTranslation("clients.giveCoupon", language) || "Give coupon"}
                        </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pt-4 border-t border-gray-100 dark:border-[#30363d]">
                        {selectedClient.fullName && (
                            <div className="flex items-center">
                                <svg
                                    className="w-5 h-5 text-gray-400 dark:text-[#8b949e] mr-3"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2.5}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-[#8b949e] font-medium">
                                        {getTranslation("clients.fullName", language) ||
                                            "Full name"}
                                    </p>
                                    <p className="text-gray-900 dark:text-[#c9d1d9] font-semibold">
                                        {selectedClient.fullName}
                                    </p>
                                </div>
                            </div>
                        )}
                        {selectedClient.phoneNumber && (
                            <div className="flex items-center">
                                <svg
                                    className="w-5 h-5 text-gray-400 dark:text-[#8b949e] mr-3"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2.5}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                    />
                                </svg>
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-[#8b949e] font-medium">
                                        {getTranslation("clients.phoneNumber", language) ||
                                            "Phone Number"}
                                    </p>
                                    <p className="text-gray-900 dark:text-[#c9d1d9] font-semibold">
                                        {selectedClient.phoneNumber}
                                    </p>
                                </div>
                            </div>
                        )}
                        {selectedClient.email && (
                            <div className="flex items-start">
                                <svg
                                    className="w-5 h-5 text-gray-400 dark:text-[#8b949e] mr-3 mt-0.5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2.5}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                    />
                                </svg>
                                <div className="flex-1">
                                    <p className="text-xs text-gray-500 dark:text-[#8b949e] font-medium mb-1">
                                        {getTranslation("clients.email", language) || "Email"}
                                    </p>
                                    <p className="text-gray-900 dark:text-[#c9d1d9] font-semibold break-all">
                                        {selectedClient.email}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {selectedClient.telegram && (
                        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-[#30363d]">
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                                <h3 className="text-sm font-bold text-gray-900 dark:text-[#c9d1d9]">
                                    {getTranslation("clients.telegramInfo", language) ||
                                        "Telegram"}
                                </h3>
                                <div className="flex flex-wrap items-center gap-2">
                                    <span
                                        className={`text-xs font-semibold px-2 py-1 rounded-md ${
                                            selectedClient.telegram.isVerified
                                                ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
                                                : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                                        }`}
                                    >
                                        {selectedClient.telegram.isVerified
                                            ? getTranslation("clients.verified", language) ||
                                              "Verified"
                                            : getTranslation("clients.notVerified", language) ||
                                              "Not verified"}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            navigate(`/chat`, {
                                                state: {
                                                    selectedCustomerId: selectedClient.telegram.id
                                                }
                                            })
                                        }
                                        className="inline-flex items-center justify-center p-1.5 bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                                        title={
                                            getTranslation(
                                                "orderContentPage.chatWithCustomer",
                                                language
                                            ) || "Chat with customer"
                                        }
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            strokeWidth={2}
                                            stroke="currentColor"
                                            className="w-5 h-5"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.436 3 12c0 2.206 1.129 4.195 2.923 5.378.134.088.225.248.18.409l-.499 1.77a.75.75 0 00.916.916l1.77-.499a.466.466 0 01.409.18C9.805 21.121 10.894 21.25 12 21.25z"
                                            />
                                        </svg>
                                    </button>
                                    {needsVerification && (
                                        <button
                                            type="button"
                                            disabled={verifyingTelegram}
                                            onClick={handleVerifyTelegram}
                                            className="px-3 py-1.5 text-xs font-semibold rounded-md bg-customOrange text-white hover:opacity-90 disabled:opacity-50"
                                        >
                                            {verifyingTelegram
                                                ? getTranslation(
                                                      "clients.verifying",
                                                      language
                                                  ) || "Verifying…"
                                                : getTranslation("clients.verify", language) ||
                                                  "Verify"}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                                {selectedClient.telegram.telegramId != null && (
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-[#8b949e] font-medium">
                                            {getTranslation("clients.telegramId", language) ||
                                                "Telegram ID"}
                                        </p>
                                        <p className="text-gray-900 dark:text-[#c9d1d9] font-medium break-all">
                                            {String(selectedClient.telegram.telegramId)}
                                        </p>
                                    </div>
                                )}
                                {selectedClient.telegram.language && (
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-[#8b949e] font-medium">
                                            {getTranslation(
                                                "clients.telegramLanguage",
                                                language
                                            ) || "Language"}
                                        </p>
                                        <p className="text-gray-900 dark:text-[#c9d1d9] font-medium">
                                            {selectedClient.telegram.language}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {clientStats.totalOrders > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
                        <div className="bg-white dark:bg-[#161b22] rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-[#30363d] shadow-sm">
                            <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-[#c9d1d9]">
                                {clientStats.totalOrders}
                            </p>
                            <p className="text-xs text-gray-600 dark:text-[#8b949e] font-medium mt-1 leading-tight">
                                {getTranslation("clients.totalOrders", language) ||
                                    "Total Orders"}
                            </p>
                        </div>
                        <div className="bg-white dark:bg-[#161b22] rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-[#30363d] shadow-sm">
                            <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-[#c9d1d9] break-words">
                                {formatPrice(clientStats.totalSpent)}
                            </p>
                            <p className="text-xs text-gray-600 dark:text-[#8b949e] font-medium mt-1 leading-tight">
                                {getTranslation("clients.totalRevenue", language) ||
                                    getTranslation("clients.totalSpent", language) ||
                                    "Total Revenue"}
                            </p>
                        </div>
                        <div className="bg-white dark:bg-[#161b22] rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-[#30363d] shadow-sm">
                            <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-[#c9d1d9] break-words">
                                {formatPrice(clientStats.averageOrderValue)}
                            </p>
                            <p className="text-xs text-gray-600 dark:text-[#8b949e] font-medium mt-1 leading-tight">
                                {getTranslation("clients.avgOrderValue", language) ||
                                    "Avg Order"}
                            </p>
                        </div>
                        <div className="bg-white dark:bg-[#161b22] rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-[#30363d] shadow-sm">
                            <p className="text-sm sm:text-lg font-bold text-gray-900 dark:text-[#c9d1d9]">
                                {clientStats.lastOrderDate
                                    ? clientStats.lastOrderDate.toLocaleDateString(
                                          language === "am"
                                              ? "hy-AM"
                                              : language === "ru"
                                                ? "ru-RU"
                                                : "en-US"
                                      )
                                    : "—"}
                            </p>
                            <p className="text-xs text-gray-600 dark:text-[#8b949e] font-medium mt-1 leading-tight">
                                {getTranslation("clients.lastOrder", language) || "Last order"}
                            </p>
                        </div>
                    </div>
                )}

                <div>
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-[#c9d1d9] mb-4">
                        {getTranslation("clients.clientOrders", language) || "Client Orders"}
                    </h2>
                    {(selectedClient.orderIds || []).length > 0 ? (
                        <div className="space-y-3">
                            {(selectedClient.orderIds || []).map((orderId) => (
                                <div
                                    key={orderId}
                                    className="bg-white dark:bg-[#161b22] rounded-lg shadow-sm p-4 sm:p-5 border border-gray-100 dark:border-[#30363d] flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3"
                                >
                                    <span className="text-base sm:text-lg font-bold text-gray-900 dark:text-[#c9d1d9]">
                                        #{orderId}
                                    </span>
                                    <button
                                        type="button"
                                        className="flex items-center justify-center w-full sm:w-auto px-4 py-2.5 bg-gray-900 dark:bg-[#161b22] text-white dark:text-[#c9d1d9] rounded-lg hover:bg-gray-800 dark:hover:bg-[#1c2128] transition-colors text-sm font-semibold whitespace-nowrap border dark:border-[#30363d]"
                                        onClick={() => handleViewOrder(orderId)}
                                    >
                                        {getTranslation("orderPage.viewFullOrder", language) ||
                                            "View Order"}
                                        <img
                                            className="w-4 h-4 ml-2 dark:invert dark:opacity-80"
                                            src={chevronRight || ""}
                                            alt=""
                                        />
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 px-4 bg-white dark:bg-[#161b22] rounded-lg shadow-sm border dark:border-[#30363d]">
                            <p className="text-base font-medium text-gray-500 dark:text-[#b1bac4]">
                                {getTranslation("clients.noOrders", language) ||
                                    "No orders found for this client"}
                            </p>
                        </div>
                    )}
                </div>

                <Toast ref={toastRef} />
            </div>
        );
    }

    return (
        <div className="pl-2">
            <h1 className="text-xl font-bold mb-4 text-gray-900 dark:text-[#c9d1d9]">
                {getTranslation("menuItems.clients", language)}
            </h1>

            {hasTelegramBot ? (
                <div className="flex border-b border-gray-200 dark:border-[#30363d] mb-4">
                    <button
                        type="button"
                        className={`py-2 px-4 font-semibold text-sm ${
                            activeTab === "clients"
                                ? "border-b-2 border-customOrange text-customOrange"
                                : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                        }`}
                        onClick={() => setActiveTab("clients")}
                    >
                        {getTranslation("menuItems.clients", language) || "Clients"}
                    </button>
                    <button
                        type="button"
                        className={`py-2 px-4 font-semibold text-sm ${
                            activeTab === "companies"
                                ? "border-b-2 border-customOrange text-customOrange"
                                : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                        }`}
                        onClick={() => setActiveTab("companies")}
                    >
                        {getTranslation("clients.companies", language) || "Companies"}
                    </button>
                </div>
            ) : null}

            {hasTelegramBot && activeTab === "companies" ? (
                <CompanyManagement />
            ) : (
                <>
                    {hasCompanies && (
                        <div className="mb-4">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-[#c9d1d9] mb-2">
                                {getTranslation("clients.byCompany", language) ||
                                    "Clients by company"}
                            </h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                                {companySummary.map((row) => {
                                    const value = row.companyId == null ? NO_COMPANY : String(row.companyId);
                                    const active = companyFilter === value;
                                    return (
                                        <button
                                            key={value}
                                            type="button"
                                            onClick={() => selectCompany(active ? "" : value)}
                                            className={`text-left bg-white dark:bg-[#161b22] rounded-lg p-3 border shadow-sm transition-colors ${
                                                active
                                                    ? "border-customOrange ring-1 ring-customOrange"
                                                    : "border-gray-200 dark:border-[#30363d] hover:border-gray-300 dark:hover:border-[#484f58]"
                                            }`}
                                        >
                                            <p className="text-sm font-bold text-gray-900 dark:text-[#c9d1d9] truncate">
                                                {row.companyName ||
                                                    getTranslation("clients.noCompany", language) ||
                                                    "No company"}
                                            </p>
                                            <p className="text-xs text-gray-600 dark:text-[#8b949e] font-medium mt-1">
                                                {row.clientsCount}{" "}
                                                {getTranslation("clients.clients", language) || "clients"}
                                            </p>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-2 mb-3">
                        {hasCompanies && (
                            <select
                                value={companyFilter}
                                onChange={(e) => selectCompany(e.target.value)}
                                aria-label={getTranslation("clients.company", language) || "Company"}
                                className="px-3 py-2 text-sm bg-white dark:bg-[#0d1117] border border-gray-300 dark:border-[#30363d] rounded-lg text-gray-900 dark:text-[#c9d1d9] font-medium"
                            >
                                <option value="">
                                    {getTranslation("clients.allCompanies", language) || "All companies"}
                                </option>
                                {companySummary
                                    .filter((row) => row.companyId != null)
                                    .map((row) => (
                                        <option key={row.companyId} value={String(row.companyId)}>
                                            {row.companyName} ({row.clientsCount})
                                        </option>
                                    ))}
                                <option value={NO_COMPANY}>
                                    {getTranslation("clients.noCompany", language) || "No company"}
                                </option>
                            </select>
                        )}
                        <div className="flex gap-2">
                            <select
                                value={sortBy}
                                onChange={(e) => {
                                    setSortBy(e.target.value);
                                    setSortOrder(e.target.value === "name" ? "asc" : "desc");
                                    setCurrentPage(1);
                                }}
                                aria-label={getTranslation("clients.sortBy", language) || "Sort by"}
                                className="flex-1 px-3 py-2 text-sm bg-white dark:bg-[#0d1117] border border-gray-300 dark:border-[#30363d] rounded-lg text-gray-900 dark:text-[#c9d1d9] font-medium"
                            >
                                {SORT_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {getTranslation(option.labelKey, language) || option.fallback}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                onClick={() => {
                                    setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
                                    setCurrentPage(1);
                                }}
                                title={sortOrder === "asc" ? "Ascending" : "Descending"}
                                className="px-3 py-2 text-sm bg-white dark:bg-[#0d1117] border border-gray-300 dark:border-[#30363d] rounded-lg text-gray-900 dark:text-[#c9d1d9] font-bold"
                            >
                                {sortOrder === "asc" ? "↑" : "↓"}
                            </button>
                        </div>
                    </div>

                    <div className="mb-4">
                        <input
                            type="text"
                            placeholder={
                                getTranslation("clients.searchPlaceholder", language) ||
                                "Search by phone or email..."
                            }
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="w-full px-3 sm:px-4 py-2.5 sm:py-2 text-sm sm:text-base bg-white dark:bg-[#0d1117] border border-gray-300 dark:border-[#30363d] rounded-lg focus:ring-2 focus:ring-gray-400 dark:focus:ring-[#484f58] focus:border-transparent text-gray-900 dark:text-[#c9d1d9] placeholder-gray-400 dark:placeholder-[#6e7681] font-medium"
                        />
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <LoadingSpinner />
                        </div>
                    ) : error ? (
                        <ErrorDisplay
                            error={error}
                            onRetry={() => {
                                setError(null);
                                setRefreshNonce((n) => n + 1);
                            }}
                        />
                    ) : visibleClients.length > 0 ? (
                        <>
                            <div className="flex justify-end mb-2 px-2">
                                <span className="bg-white dark:bg-[#161b22] text-gray-700 dark:text-[#c9d1d9] font-bold px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm border dark:border-[#30363d]">
                                    {getTranslation("common.total", language) || "Total"}:{" "}
                                    {companyFilter ? visibleClients.length : totalCount}
                                </span>
                            </div>
                            {(searchText || companyFilter) && (
                                <div className="bg-blue-50 dark:bg-blue-950/30 text-blue-800 dark:text-[#58a6ff] px-3 sm:px-4 py-2 rounded-lg mb-4 text-xs sm:text-sm border border-blue-200 dark:border-blue-800">
                                    <span className="block sm:inline font-medium">
                                        {getTranslation("clients.showing", language) ||
                                            "Showing"}{" "}
                                        {visibleClients.length}{" "}
                                        {getTranslation("clients.of", language) || "of"}{" "}
                                        {companyFilter ? visibleClients.length : totalCount}{" "}
                                        {getTranslation("clients.clients", language) ||
                                            "clients"}
                                    </span>
                                    <button
                                        type="button"
                                        className="block sm:inline ml-0 sm:ml-2 mt-1 sm:mt-0 text-blue-600 dark:text-[#58a6ff] hover:text-blue-800 dark:hover:text-[#79c0ff] underline font-medium"
                                        onClick={() => {
                                            setSearchText("");
                                            selectCompany("");
                                        }}
                                    >
                                        {getTranslation("orderPage.clearFilters", language) ||
                                            "Clear filters"}
                                    </button>
                                </div>
                            )}
                            <div className="space-y-6">
                                {groupedClients.map((group) => (
                                    <div key={group.key}>
                                        {hasCompanies ? (
                                            <h3 className="text-sm font-bold text-gray-900 dark:text-[#c9d1d9] mb-2 px-1">
                                                {group.companyName ||
                                                    getTranslation("clients.noCompany", language) ||
                                                    "No company"}
                                                <span className="ml-2 text-xs font-medium text-gray-500 dark:text-[#8b949e]">
                                                    {group.clients.length}
                                                </span>
                                            </h3>
                                        ) : null}
                                        <div className="space-y-3">
                                            {group.clients.map((client) => {
                                                const orderCount =
                                                    client.ordersCount ?? client.orderIds?.length ?? 0;
                                                const clientKey = client.id ?? client.phoneNumber;
                                                return (
                                                    <div
                                                        key={clientKey}
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={() => handleClientClick(client)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter" || e.key === " ") {
                                                                e.preventDefault();
                                                                handleClientClick(client);
                                                            }
                                                        }}
                                                        className="bg-white dark:bg-[#161b22] rounded-lg shadow-sm p-3 sm:p-4 border border-gray-100 dark:border-[#30363d] cursor-pointer hover:shadow-md transition-shadow"
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex-1 min-w-0 pr-3">
                                                                <div className="space-y-1.5">
                                                                    {client.fullName && (
                                                                        <div className="flex items-center text-xs sm:text-sm text-gray-700 dark:text-[#c9d1d9] font-semibold">
                                                                            <span className="truncate">
                                                                                {client.fullName}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    {client.phoneNumber && (
                                                                        <div className="flex items-center text-xs sm:text-sm text-gray-600 dark:text-[#b1bac4]">
                                                                            <svg
                                                                                className="w-4 h-4 mr-2 flex-shrink-0"
                                                                                fill="none"
                                                                                viewBox="0 0 24 24"
                                                                                stroke="currentColor"
                                                                                strokeWidth={2.5}
                                                                            >
                                                                                <path
                                                                                    strokeLinecap="round"
                                                                                    strokeLinejoin="round"
                                                                                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                                                                />
                                                                            </svg>
                                                                            <span className="truncate font-medium">
                                                                                {client.phoneNumber}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    {client.email && (
                                                                        <div className="flex items-start text-xs sm:text-sm text-gray-600 dark:text-[#b1bac4]">
                                                                            <svg
                                                                                className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5"
                                                                                fill="none"
                                                                                viewBox="0 0 24 24"
                                                                                stroke="currentColor"
                                                                                strokeWidth={2.5}
                                                                            >
                                                                                <path
                                                                                    strokeLinecap="round"
                                                                                    strokeLinejoin="round"
                                                                                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                                                                />
                                                                            </svg>
                                                                            <span className="break-all font-medium">
                                                                                {client.email}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    <div className="flex items-center text-xs sm:text-sm text-gray-700 dark:text-[#c9d1d9] font-semibold">
                                                                        <svg
                                                                            className="w-4 h-4 mr-2 flex-shrink-0"
                                                                            fill="none"
                                                                            viewBox="0 0 24 24"
                                                                            stroke="currentColor"
                                                                            strokeWidth={2.5}
                                                                        >
                                                                            <path
                                                                                strokeLinecap="round"
                                                                                strokeLinejoin="round"
                                                                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                                                            />
                                                                        </svg>
                                                                        <span>
                                                                            {orderCount}{" "}
                                                                            {getTranslation(
                                                                                "clients.totalOrders",
                                                                                language
                                                                            ) || "Orders"}
                                                                            {" · "}
                                                                            {formatPrice(
                                                                                client.purchasedTotalSum ?? 0
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-[#8b949e] font-medium">
                                                                        <span>
                                                                            {getTranslation(
                                                                                "clients.lastOrder",
                                                                                language
                                                                            ) || "Last order"}
                                                                            {": "}
                                                                            {formatDate(client.latestOrderDate)}
                                                                        </span>
                                                                        {client.companyName && (
                                                                            <span className="px-2 py-0.5 rounded-md bg-gray-100 dark:bg-[#0d1117] text-gray-700 dark:text-[#b1bac4] border dark:border-[#30363d]">
                                                                                {client.companyName}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleGiveCoupon(client);
                                                                }}
                                                                onKeyDown={(e) => e.stopPropagation()}
                                                                className="px-3 py-1.5 text-xs font-semibold rounded-md bg-customOrange text-white hover:opacity-90 whitespace-nowrap flex-shrink-0"
                                                            >
                                                                {getTranslation(
                                                                    "clients.giveCoupon",
                                                                    language
                                                                ) || "Give coupon"}
                                                            </button>
                                                            <img
                                                                className="w-4 h-4 sm:w-5 sm:h-5 ml-2 flex-shrink-0 dark:invert dark:opacity-80"
                                                                src={chevronRight || ""}
                                                                alt=""
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {totalCount > 0 && (
                                <div className="flex flex-col sm:flex-row justify-between items-center mt-6 mb-12 px-2">
                                    <div className="mb-4 sm:mb-0">
                                        <RowsPerPageSelect
                                            label={
                                                getTranslation("common.rowsPerPage", language) ||
                                                "Rows per page"
                                            }
                                            value={itemsPerPage}
                                            onChange={(value) => {
                                                setItemsPerPage(value);
                                                setCurrentPage(1);
                                            }}
                                            margin=""
                                        />
                                    </div>

                                    <Pagination
                                        totalItems={totalCount}
                                        itemsPerPage={itemsPerPage}
                                        currentPage={currentPage}
                                        onPageChange={setCurrentPage}
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-12 px-4 bg-white dark:bg-[#161b22] rounded-lg shadow-sm border dark:border-[#30363d]">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-[#0d1117] mb-4 border dark:border-[#30363d]">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-8 w-8 text-gray-400 dark:text-[#8b949e]"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2.5}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                    />
                                </svg>
                            </div>
                            <p className="text-base font-medium text-gray-500 dark:text-[#b1bac4]">
                                {getTranslation("clients.noClients", language) ||
                                    "No clients found"}
                            </p>
                            {(searchText || companyFilter) && (
                                <button
                                    type="button"
                                    className="mt-3 text-sm text-blue-600 dark:text-[#58a6ff] hover:text-blue-800 dark:hover:text-[#79c0ff] underline font-medium"
                                    onClick={() => {
                                        setSearchText("");
                                        selectCompany("");
                                    }}
                                >
                                    {getTranslation("orderPage.clearFilters", language) ||
                                        "Clear filters"}
                                </button>
                            )}
                        </div>
                    )}
                </>
            )}

            <Toast ref={toastRef} />
        </div>
    );
};

export default Clients;
